import { useCallback, useEffect, useMemo, useRef, useState, type Key } from 'react';
import { Icon } from '@iconify/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CustomTabs,
  CtySpinner,
  ErrorBoundary,
  PageHeader,
  checkType,
  CtyShowToast,
} from '@circuitry-ai/doc-ui';
import { RouteState, useGetMapperByIdQuery, type MapperItem, useTypedSelector } from '@circuitry-ai/doc-data';
import { useTranslation } from 'react-i18next';
import SchemaMapperConfiguration from './SchemaMapperConfiguration';
import SchemaMapperMapper from './SchemaMapperMapper';
import SchemaMapperExecute from './SchemaMapperExecute';

enum SchemaMapperTabs {
  CONFIGURATION = 'configuration',
  MAPPER = 'mapper',
  EXECUTE = 'execute',
}

const PageHeaderWithErrorBoundary = ErrorBoundary(PageHeader);
const CustomTabsWithErrorBoundary = ErrorBoundary(CustomTabs);
const SchemaMapperConfigurationWithErrorBoundary = ErrorBoundary(SchemaMapperConfiguration);
const SchemaMapperMapperWithErrorBoundary = ErrorBoundary(SchemaMapperMapper);
const SchemaMapperExecuteWithErrorBoundary = ErrorBoundary(SchemaMapperExecute);

export default function SchemaMapperView() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const token = useTypedSelector((state) => state.auth.token);
  const [skip, setSkip] = useState<boolean>(true);
  const { mode, mapper_id } = useParams<{ mode: string; mapper_id?: string }>();
  const queryParams = new URLSearchParams(location.search);
  const tab = queryParams.get('tab');
  const [currentTab, setCurrentTab] = useState<string>(tab ?? SchemaMapperTabs.CONFIGURATION);
  const tabContentRef = useRef<HTMLDivElement | null>(null);
  const [isTabFullscreen, setIsTabFullscreen] = useState(false);

  const locationState = (location.state ?? {}) as { mapper?: MapperItem };
  const mapperFromState = locationState.mapper ?? null;
  const [mapperSnapshot, setMapperSnapshot] = useState<MapperItem | null>(mapperFromState);

  useEffect(() => {
    if (token && skip) {
      setSkip(false);
    }
  }, [skip, token]);

  const shouldFetchMapper = mode === RouteState.UPDATE && Boolean(mapper_id);

  const { data: mapperByIdResponse, isLoading: isMapperLoading, isFetching: isMapperFetching } = useGetMapperByIdQuery(mapper_id ?? '', {
    skip: skip || !shouldFetchMapper,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (mapperByIdResponse) {
      setMapperSnapshot(mapperByIdResponse);
      return;
    }

    if (mapperFromState) {
      setMapperSnapshot(mapperFromState);
    }
  }, [mapperByIdResponse, mapperFromState]);

  const activeMapper = useMemo(
    () => mapperSnapshot ?? mapperByIdResponse ?? mapperFromState ?? null,
    [mapperSnapshot, mapperByIdResponse, mapperFromState]
  );
  const hasMapperMappings = Boolean(activeMapper?.mapper_definition?.mappings?.length);
  const isMapperBootstrapLoading = shouldFetchMapper && skip && !hasMapperMappings;
  const isMapperDataLoading =
    shouldFetchMapper &&
    !hasMapperMappings &&
    (isMapperBootstrapLoading || isMapperLoading || isMapperFetching);

  useEffect(() => {
    if (tab) setCurrentTab(tab);
    else setCurrentTab(SchemaMapperTabs.CONFIGURATION);
  }, [tab]);

  useEffect(() => {
    if (currentTab === SchemaMapperTabs.MAPPER) return;
    if (document.fullscreenElement === tabContentRef.current) {
      void document.exitFullscreen();
    }
  }, [currentTab]);

  const onTabChange = (nextTab: Key) => {
    const next = String(nextTab);
    setCurrentTab(next);
    const base = mapper_id ? `/settings/schema-mapper/${mode}/${mapper_id}` : `/settings/schema-mapper/${mode}`;
    navigate(`${base}?tab=${next}`, { state: activeMapper ? { mapper: activeMapper } : undefined });
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsTabFullscreen(document.fullscreenElement === tabContentRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleTabFullscreen = useCallback(async () => {
    const tabContent = tabContentRef.current;
    if (!tabContent) return;

    try {
      if (document.fullscreenElement === tabContent) {
        await document.exitFullscreen();
        return;
      }

      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }

      await tabContent.requestFullscreen();
    } catch {
      CtyShowToast(checkType.error, t('Unable to toggle fullscreen mode'));
    }
  }, [t]);

  const tabs = useMemo(
    () => [
      {
        key: SchemaMapperTabs.CONFIGURATION,
        title: t('Configuration'),
        id: 'schema-mapper-configuration-tab',
        isDisabled: false,
      },
      {
        key: SchemaMapperTabs.MAPPER,
        title: t('Mapper'),
        id: 'schema-mapper-mapper-tab',
        isDisabled: mode === RouteState.CREATE || !activeMapper?.aiworker_id,
        endContent: activeMapper?.aiworker_id ? (
          <Icon
            icon={
              isTabFullscreen
                ? 'material-symbols:fullscreen-exit-rounded'
                : 'material-symbols:fullscreen'
            }
            width="18"
            height="18"
            className="shrink-0"
            onClick={(event) => {
              event.stopPropagation();
              void toggleTabFullscreen();
            }}
          />
        ) : undefined,
      },
      {
        key: SchemaMapperTabs.EXECUTE,
        title: t('Execute'),
        id: 'schema-mapper-execute-tab',
        isDisabled: mode === RouteState.CREATE || !activeMapper?.aiworker_id,
      },
    ],
    [activeMapper?.aiworker_id, isTabFullscreen, mode, t, toggleTabFullscreen]
  );

  const renderSelectedTab = () => {
    switch (currentTab) {
      case SchemaMapperTabs.CONFIGURATION:
        return <SchemaMapperConfigurationWithErrorBoundary mode={mode} mapper={activeMapper} />;
      case SchemaMapperTabs.MAPPER:
        return (
          <SchemaMapperMapperWithErrorBoundary
            mapper={activeMapper}
            onMapperChange={setMapperSnapshot}
            isMapperDataLoading={isMapperDataLoading}
          />
        );
      case SchemaMapperTabs.EXECUTE:
        return <SchemaMapperExecuteWithErrorBoundary mapper={activeMapper} />;
    }
    return <SchemaMapperConfigurationWithErrorBoundary mode={mode} mapper={activeMapper} />;
  };

  return (
    <div className="flex flex-col h-full gap-2">
      <PageHeaderWithErrorBoundary
        heading={t('Configure')}
        description={t('Create and update mapper configuration.')}
        pageHeaderClass="pb-2"
      />
      <div className="px-5">
        <CustomTabsWithErrorBoundary
          tabs={tabs}
          selectedKey={currentTab}
          onTabChange={onTabChange}
          disabledKeys={[]}
        />
      </div>
      <div
        ref={tabContentRef}
        className={`flex-1 min-h-0 overflow-hidden ${
          isTabFullscreen
            ? 'box-border bg-content1 [&:fullscreen]:flex [&:fullscreen]:h-full [&:fullscreen]:w-full [&:fullscreen]:flex-col [&:fullscreen]:p-3'
            : ''
        }`}
      >
        {shouldFetchMapper && (isMapperLoading || isMapperFetching) ? (
          <div className="flex h-full w-full items-center justify-center">
            <CtySpinner size="md" />
          </div>
        ) : (
          renderSelectedTab()
        )}
      </div>
    </div>
  );
}

export { SchemaMapperView };
